import {
  TemplateActivities,
  TemplateConvertDirection,
  TemplateMonthAmount,
  TemplateMonthBoxState,
  TemplateSaveChoice,
  TemplateSaveGuard,
  TemplateUnitChangeProps,
} from "../../../models/TemplateActivities";
import { Activity } from "../../../models/Activities";
import { EmissionFactorDataV2 } from "../../../models/EmissionFactors";
import { useEffect, useState } from "react";
import {
  AddEmissionContext,
  EmissionMode,
  FormValuesStepTwo,
} from "../../../contexts/AddEmissionContext";
import CustomModal from "../../../components/modal/CustomModal";
import { Typography, Box, Button, CircularProgress } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { DROPDOWN_EMISSION_GROUP } from "../../../components/emission/emission-components";
import dayjs, { Dayjs } from "dayjs";
import alertService from "../../../components/alert/alertService";
import { Trans } from "react-i18next";
import TemplateActivitiesService from "../../../service/template-activities.service";
import ActivitiesService from "../../../service/activities.service";
import { MONTHS } from "../../../constants/app-config";
import { ActivityTemplatesContext } from "../../../contexts/ActivityTemplatesContext";
import { useAuth } from "../../../contexts/UserContext";
import { OrganizationBreadcrumbs } from "../../emission/_components/OrganizationBreadcrumbs";
import AddEmissionStepTwo from "../../emission/_components/popup-add-emission/step2/AddEmissionStepTwo";

interface Props {
  row: TemplateActivities;
  open: boolean;
  onClose: () => void;
}

export const EditActivityTemplates = (props: Props) => {
  const { organization } = useAuth();

  const {
    stepOneForm,
    stepTwoForm,
    subOrganizationList,
    assetList,
    setEmissionMode,
    setDisableMonthly,
    prepareFormDataToCreateOrDraftTemplates,
    refreshEmissionFactors,
    emissionFactorsMasterData,
  } = AddEmissionContext();

  const { transformersToDataGrid } = ActivityTemplatesContext();

  const confirmUnchangedUnit = () =>
    alertService.confirm(
      t("Edit"),
      <Box>
        <Trans
          t={t}
          i18nKey="edit-templates"
          values={{ id: props.row.id }}
          components={{
            span: <span style={{ color: "var(--red)" }} />,
          }}
        />

        <Typography variant="text2" display="block" mt={1} color="var(--red)">
          <Trans t={t} i18nKey="edit-templates-emission-warning" />
        </Typography>
      </Box>,
      t("Save"),
    );

  const askUnitChange = async (
    fromUnit: string,
    toUnit: string,
    toFactorRef: string | null,
  ): Promise<TemplateSaveChoice> => {
    const amountFromDetails =
      DROPDOWN_EMISSION_GROUP.find(
        (g) => g.emissionGroup === props.row.emissionGroup,
      )?.canQuickUpdateAmount === false;
    const direction = amountFromDetails
      ? null
      : convertDirection(
          emissionFactorsMasterData,
          props.row.emissionFactorRef,
          toFactorRef,
        );

    if (direction) {
      const answer = await alertService.choose(
        <PopupTitle
          text={t("edit-templates-convert-title", { unit: toUnit })}
        />,
        <ConvertOffer
          row={props.row}
          from={fromUnit}
          to={toUnit}
          direction={direction}
        />,
        t("edit-templates-convert-save", { unit: toUnit }),
        t("edit-templates-convert-save-plain"),
      );
      return answer === "confirm"
        ? "convert"
        : answer === "deny"
        ? "save"
        : "cancel";
    }

    const isConfirmed = await alertService.confirm(
      <PopupTitle text={t("edit-templates-unit-title")} />,
      <UnitChangeWarning
        row={props.row}
        from={fromUnit}
        to={toUnit}
        amountFromDetails={amountFromDetails}
      />,
      t("Save"),
      undefined,
      undefined,
      undefined,
      t("Cancel"),
      "var(--white)",
    );
    return isConfirmed ? "save" : "cancel";
  };

  const onSubmit = async (_data: FormValuesStepTwo) => {
    const formData = prepareFormDataToCreateOrDraftTemplates(
      props.row.lastGeneratedAt === null ? false : true,
    );
    const fromUnit = (props.row.unit ?? "").trim();
    const toUnit = (stepTwoForm.getValues("unit") ?? "").trim();

    const choice: TemplateSaveChoice =
      fromUnit === "" || toUnit === fromUnit
        ? (await confirmUnchangedUnit())
          ? "save"
          : "cancel"
        : await askUnitChange(fromUnit, toUnit, formData.emissionFactorRef);
    if (choice === "cancel") return;

    // a template whose factor record is gone lists a null ref, which the server refuses
    const guard: TemplateSaveGuard = props.row.emissionFactorRef
      ? { expectedEmissionFactorRef: props.row.emissionFactorRef }
      : {};
    if (choice === "convert") guard.convertAmounts = true;

    let result;
    try {
      alertService.showLoading();
      result = await TemplateActivitiesService.editTemplateActivities(
        { ...formData, ...guard },
        props.row._id,
      );
    } catch {
      // error alert already shown by service
      return;
    } finally {
      alertService.closeLoading();
    }

    if (result) {
      transformersToDataGrid(result.templateActivities);
      alertService.mixin(t("Successfully"));
      close();
    }
  };

  const close = () => {
    props.onClose();
    setTimeout(() => {
      stepTwoForm.reset();
      stepOneForm.reset();
    }, 300);
  };

  const setUpForm = (templateYear: Dayjs) => {
    stepTwoForm.setValue("activityGroup", props.row.activityGroupEN ?? "");
    stepTwoForm.setValue("activityType", props.row.activityTypeEn);
    stepTwoForm.setValue("activityAttribute", props.row.activityAttributeEN);
    // the stored factor only fits the stored year; a move during load keeps the new year's default
    if (dayjs(stepTwoForm.getValues("year")).isSame(templateYear, "year")) {
      stepTwoForm.setValue(
        "activityEmissionFactorPublisher",
        props.row.emissionFactorRef ?? "",
      );
    }
    stepTwoForm.setValue("amount", props.row.amount.toString());
    stepTwoForm.setValue("unit", props.row.unit ?? "");
    stepTwoForm.setValue("remark", props.row.remark ?? "");

    if (props.row.auxiliary) {
      stepTwoForm.setValue("aux1", props.row.auxiliary.aux1 ?? "");
      stepTwoForm.setValue("aux2", props.row.auxiliary.aux2 ?? "");
      stepTwoForm.setValue("aux3", props.row.auxiliary.aux3 ?? "");
      stepTwoForm.setValue("aux4", props.row.auxiliary.aux4 ?? "");
      stepTwoForm.setValue("aux5", props.row.auxiliary.aux5 ?? "");
    }

    stepTwoForm.setValue("monthly", props.row.isMonthly ?? false);
    stepTwoForm.setValue("vendor", props.row.vendor ?? "");
  };

  useEffect(() => {
    if (!props.open) {
      setDisableMonthly(false);
      return;
    }

    setEmissionMode(EmissionMode.AddActivityTemplates);
    setDisableMonthly(!!props.row.lastGeneratedAt);

    const initData = async () => {
      const item = subOrganizationList.find((i) => {
        return i.name.trim() === props.row.suborganizationName.trim();
      });

      stepOneForm.setValue("subOrganizations", item?.id as string);
      stepOneForm.setValue(
        "location",
        `${item?.location?.address ?? ""} ${item?.location?.district ?? ""} ${
          item?.location?.city ?? ""
        }`.trim(),
      );

      const emissionGroupRef: string | undefined = DROPDOWN_EMISSION_GROUP.find(
        (i) => i.emissionGroup === props.row.emissionGroup,
      )?.emissionGroup;

      stepOneForm.setValue("emissionGroup", emissionGroupRef as string);
      stepOneForm.setValue(
        "assetName",
        assetList.find(
          (i) =>
            i.name === props.row.assetName &&
            i.suborganizationName === props.row.suborganizationName &&
            i.siteName === props.row.siteName,
        )?._id ?? "",
      );

      const templateYear = dayjs(`${props.row.year}-06-01`);
      stepTwoForm.setValue("year", templateYear);
      stepTwoForm.setValue("startDate", templateYear.startOf("year"));
      stepTwoForm.setValue("endDate", templateYear.endOf("year"));

      if (emissionGroupRef) {
        await refreshEmissionFactors();
      }

      setTimeout(() => {
        setUpForm(templateYear);
      }, 50);
    };

    initData();
  }, [props.open]);

  const OnError = (errors: any) => {
    const inputError: string[] = Object.keys(errors);
    const form: HTMLElement | null = document.getElementById(inputError[0]);

    if (form)
      form.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
  };

  return (
    <CustomModal
      header={
        <Box>
          <Typography variant="h1">
            {t("edit-templates_hl")}
            {` AT-${props.row.id}`}{" "}
          </Typography>
          <OrganizationBreadcrumbs
            organization={organization?.name ?? ""}
            location={stepOneForm.getValues("location") ?? ""}
            subOrganizations={
              subOrganizationList.find(
                (i) => i.id === stepOneForm.getValues("subOrganizations"),
              )?.name ?? ""
            }
            site={subOrganizationList
              .find((i) => i.id === stepOneForm.getValues("subOrganizations"))
              ?.sites.find((site) => site.name === props.row.siteName)}
          />
        </Box>
      }
      body={
        <Box height={"80dvh"}>
          <FormProvider {...stepTwoForm}>
            <form
              onSubmit={stepTwoForm.handleSubmit(onSubmit, OnError)}
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <AddEmissionStepTwo />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  marginTop: "44px",
                }}
                rowGap={2.5}
              >
                <Button
                  fullWidth
                  color="secondary"
                  variant="contained"
                  type="submit"
                  name="add"
                >
                  {t("Save")}
                </Button>
              </Box>
            </form>
          </FormProvider>
        </Box>
      }
      open={props.open}
      onClose={close}
    />
  );
};

// the data entry grid shows amounts to 4 decimals
const AMOUNT_DECIMALS = 4;
// 1 tonne = 10^3 kg
const KG_PER_TONNE_EXPONENT = 3;
const EXAMPLE_AMOUNT = 5;
// label + row + note + padding of the filled box, so Save does not jump when the month arrives
const WARNING_BOX_MIN_HEIGHT = 100;
// label + two grid rows of the filled convert box
const OFFER_ROWS_MIN_HEIGHT = 84;

export const formatAmount = (amount: number): string =>
  amount.toLocaleString("en-US", { maximumFractionDigits: AMOUNT_DECIMALS });

const midpointOf = (a: Activity) => {
  const start = dayjs(a.startDate).valueOf();
  return dayjs(start + (dayjs(a.endDate).valueOf() - start) / 2);
};

// months are stored at the server's local midnight; the middle of the month names it in any browser time zone
export const firstMonthWithAmount = (
  activities: Activity[],
  emissionFactorRef: string | undefined,
  year: number,
): TemplateMonthAmount | null => {
  const first = activities
    .filter(
      (a) =>
        a.generatedByTemplate === true &&
        a.emissionFactorRef === emissionFactorRef,
    )
    .map((a) => ({ mid: midpointOf(a), amount: a.amount }))
    .filter(
      ({ mid, amount }) =>
        mid.year() === year && Number(amount.toFixed(AMOUNT_DECIMALS)) > 0,
    )
    .sort((x, y) => x.mid.valueOf() - y.mid.valueOf())[0];
  return first ? { month: first.mid.month(), amount: first.amount } : null;
};

// "129" -> "130" without going through a float
const incrementDigits = (digits: string): string => {
  const nines = digits.search(/9*$/);
  if (nines === 0) return "1" + "0".repeat(digits.length);
  return (
    digits.slice(0, nines - 1) +
    String(Number(digits[nines - 1]) + 1) +
    "0".repeat(digits.length - nines)
  );
};

// the server's Decimal half-up at 4 decimals, done on the digits so the dialog never shows a number the server won't store
export const convertAmount = (
  amount: number,
  direction: TemplateConvertDirection,
): number => {
  const [mantissa, exponent] = amount.toExponential().split("e");
  const digits = mantissa.replace(".", "");
  const shift =
    direction === "tonne-to-kg"
      ? KG_PER_TONNE_EXPONENT
      : -KG_PER_TONNE_EXPONENT;
  const decimals = digits.length - 1 - Number(exponent) - shift;
  if (decimals <= AMOUNT_DECIMALS) {
    return Number(
      `${digits}${"0".repeat(AMOUNT_DECIMALS - decimals)}e-${AMOUNT_DECIMALS}`,
    );
  }
  const dropped = decimals - AMOUNT_DECIMALS;
  const padded = digits.padStart(dropped + 1, "0");
  const kept = padded.slice(0, padded.length - dropped);
  const rounded =
    padded[padded.length - dropped] >= "5" ? incrementDigits(kept) : kept;
  return Number(`${rounded}e-${AMOUNT_DECIMALS}`);
};

// the link sits on the tonne factor and names its exact kg twin
export const convertDirection = (
  groups: EmissionFactorDataV2[],
  fromRef: string | undefined,
  toRef: string | null,
): TemplateConvertDirection | null => {
  const publishers = groups.flatMap((g) => g.publishers);
  const from = publishers.find((p) => p.emissionFactorRefId === fromRef);
  const to = publishers.find((p) => p.emissionFactorRefId === toRef);
  if (!from || !to) return null;
  if (from.reportAs === to.emissionFactorRefId) return "tonne-to-kg";
  if (to.reportAs === from.emissionFactorRefId) return "kg-to-tonne";
  return null;
};

// the dialog's React root is unmounted on close, so a late answer is dropped by React
const useFirstMonth = (row: TemplateActivities, amountFromDetails: boolean) => {
  const [state, setState] = useState<TemplateMonthBoxState>(
    amountFromDetails
      ? "example-aux"
      : row.assetRef
      ? "checking"
      : "example-failed",
  );
  const [month, setMonth] = useState<TemplateMonthAmount | null>(null);

  useEffect(() => {
    if (state !== "checking" || !row.assetRef) return;
    ActivitiesService.searchTemplateMonths({
      assetRefs: [row.assetRef],
      filterByMonthlyTemplate: true,
      startDate: `${row.year}-01-01`,
      endDate: `${row.year}-12-31`,
    }).then((activities) => {
      if (activities === null) {
        setState("example-failed");
        return;
      }
      const first = firstMonthWithAmount(
        activities,
        row.emissionFactorRef,
        row.year,
      );
      setMonth(first);
      setState(first ? "month" : "example-empty");
    });
  }, [state, row.assetRef, row.emissionFactorRef, row.year]);

  return { state, month };
};

const boxSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "4px",
  p: "12px 16px 16px",
  borderRadius: "8px",
  background: "var(--yellow-12)",
} as const;
const labelSx = {
  fontSize: 12,
  lineHeight: "16px",
  fontWeight: 500,
  color: "var(--gray1)",
} as const;
const noteSx = { ...labelSx, fontWeight: 400 } as const;

const Checking = () => (
  <>
    <Box
      component="span"
      sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}
    >
      <CircularProgress
        variant="determinate"
        value={100}
        size={22}
        thickness={5}
        aria-hidden
        sx={{ position: "absolute", color: "var(--yellow-25)" }}
      />
      <CircularProgress
        size={22}
        thickness={5}
        sx={{ color: "var(--yellow-dark)" }}
      />
    </Box>
    <Box component="span" sx={labelSx}>
      {t("edit-templates-unit-checking")}
    </Box>
  </>
);

const Arrow = ({ size }: { size: number }) => (
  <ArrowForwardRoundedIcon
    sx={{ fontSize: size, flexShrink: 0, color: "var(--gray1)" }}
  />
);

const PopupTitle = ({ text }: { text: string }) => (
  <Box
    component="span"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      pr: "32px",
      color: "var(--dark)",
    }}
  >
    <WarningAmberIcon
      sx={{ fontSize: 24, flexShrink: 0, color: "var(--yellow-dark)" }}
    />
    <span>{text}</span>
  </Box>
);

const monthLabel = (month: TemplateMonthAmount | null) =>
  month
    ? t("edit-templates-unit-your-month", { month: t(MONTHS[month.month]) })
    : t("edit-templates-unit-example");

export const UnitChangeWarning = ({
  row,
  from,
  to,
  amountFromDetails,
}: TemplateUnitChangeProps & { amountFromDetails: boolean }) => {
  const { state, month } = useFirstMonth(row, amountFromDetails);
  const amount = formatAmount(month?.amount ?? EXAMPLE_AMOUNT);

  return (
    <Box
      component="span"
      sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
    >
      {state === "checking" ? (
        <Box
          component="span"
          data-state={state}
          sx={{
            ...boxSx,
            justifyContent: "center",
            gap: "8px",
            minHeight: WARNING_BOX_MIN_HEIGHT,
            boxSizing: "border-box",
          }}
        >
          <Checking />
        </Box>
      ) : (
        <Box component="span" data-state={state} sx={boxSx}>
          <Box component="span" sx={labelSx}>
            {monthLabel(month)}
          </Box>
          <Box
            component="span"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "16px",
              fontSize: 24,
              lineHeight: "32px",
              fontWeight: 700,
              color: "var(--dark)",
            }}
          >
            <span>{`${amount} ${from}`}</span>
            <Arrow size={24} />
            <span>
              {`${amount} `}
              <Box component="span" sx={{ color: "var(--red)" }}>
                {to}
              </Box>
            </span>
          </Box>
          {month && (
            <Box component="span" sx={noteSx}>
              {t("edit-templates-unit-others")}
            </Box>
          )}
        </Box>
      )}
      <Box
        component="span"
        sx={{
          fontSize: 16,
          lineHeight: "24px",
          fontWeight: 700,
          color: "var(--dark)",
        }}
      >
        {t(
          amountFromDetails
            ? "edit-templates-unit-retype-details"
            : "edit-templates-unit-retype",
          { unit: to },
        )}
      </Box>
      <Box
        component="span"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          columnGap: "16px",
          fontSize: 14,
          lineHeight: "20px",
          fontWeight: 400,
          color: "var(--gray1)",
        }}
      >
        <span>{t("edit-templates-unit-emissions")}</span>
        <span>{t("edit-templates-unit-id", { id: row.id })}</span>
      </Box>
    </Box>
  );
};

const rowLabelSx = {
  fontSize: 14,
  lineHeight: "28px",
  fontWeight: 500,
  color: "var(--gray1)",
  textAlign: "right",
} as const;

export const ConvertOffer = ({
  row,
  from,
  to,
  direction,
}: TemplateUnitChangeProps & { direction: TemplateConvertDirection }) => {
  const { state, month } = useFirstMonth(row, false);
  const amount = month?.amount ?? EXAMPLE_AMOUNT;

  return (
    <Box
      component="span"
      sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
    >
      <Box component="span" data-state={state} sx={{ ...boxSx, gap: "8px" }}>
        {state === "checking" ? (
          <Box
            component="span"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              minHeight: OFFER_ROWS_MIN_HEIGHT,
            }}
          >
            <Checking />
          </Box>
        ) : (
          <>
            <Box component="span" sx={labelSx}>
              {monthLabel(month)}
            </Box>
            <Box
              component="span"
              sx={{
                display: "grid",
                gridTemplateColumns: "auto auto 20px auto",
                justifyContent: "center",
                alignItems: "center",
                columnGap: "14px",
                rowGap: "4px",
                fontSize: 20,
                lineHeight: "28px",
                fontWeight: 700,
                color: "var(--dark)",
              }}
            >
              <Box component="span" sx={rowLabelSx}>
                {t("edit-templates-convert-row")}
              </Box>
              <Box component="span" sx={{ textAlign: "right" }}>
                {`${formatAmount(amount)} ${from}`}
              </Box>
              <Arrow size={20} />
              <Box component="span" sx={{ color: "var(--green)" }}>
                {`${formatAmount(convertAmount(amount, direction))} ${to}`}
              </Box>
              <Box component="span" sx={rowLabelSx}>
                {t("edit-templates-convert-keep-row")}
              </Box>
              <Box component="span" sx={{ textAlign: "right" }}>
                {`${formatAmount(amount)} ${from}`}
              </Box>
              <Arrow size={20} />
              <Box component="span" sx={{ color: "var(--red)" }}>
                {`${formatAmount(amount)} ${to}`}
              </Box>
            </Box>
          </>
        )}
        <Box component="span" sx={noteSx}>
          {t("edit-templates-convert-applies")}
        </Box>
      </Box>
      <Box
        component="span"
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          fontSize: 14,
          lineHeight: "20px",
          fontWeight: 400,
          color: "var(--gray1)",
        }}
      >
        {t("edit-templates-unit-id", { id: row.id })}
      </Box>
    </Box>
  );
};
